import React from "react";
import { Plus, Package, Trash } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

export function QuotationEntityFields({
  formData,
  setFormData,
  clients,
  projects,
  getEntityId,
  formatClientDisplay,
  t,
  clientLabel,
  disabled = false,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{clientLabel || t("client")}</Label>
        <Select
          value={formData.client_id}
          onValueChange={(v) => setFormData({ ...formData, client_id: String(v) })}
          disabled={disabled}
        >
          <SelectTrigger className="valhalla-input">
            <SelectValue placeholder={t("selectClient")} />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            {clients.map((client) => (
              <SelectItem key={getEntityId(client)} value={getEntityId(client)}>
                {formatClientDisplay(client)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{t("projectOptional")}</Label>
        <Select
          value={formData.project_id || "none"}
          onValueChange={(v) =>
            setFormData({ ...formData, project_id: v === "none" ? "" : v })
          }
          disabled={disabled}
        >
          <SelectTrigger className="valhalla-input">
            <SelectValue placeholder={t("selectProject")} />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            <SelectItem value="none">{t("noProject")}</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project._id} value={project._id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function QuotationHistoricalOptions({
  formData,
  setFormData,
  getTodayDateInput,
  mode = "create",
  fallbackQuotationNumber = "",
  fallbackIssueDate = "",
  disabled = false,
}) {
  const isEdit = mode === "edit";

  return (
    <div className="rounded-lg border border-[#242E42] bg-[#0B0E14]/60 p-4 space-y-4">
      <div className="flex items-start gap-3">
        <Checkbox
          id={`${mode}-use-custom-quotation-number`}
          checked={formData.use_custom_quotation_number}
          onCheckedChange={(checked) =>
            setFormData({
              ...formData,
              use_custom_quotation_number: Boolean(checked),
              quotation_number: formData.quotation_number || fallbackQuotationNumber,
            })
          }
          disabled={disabled}
        />
        <div className="space-y-1">
          <Label
            htmlFor={`${mode}-use-custom-quotation-number`}
            className="text-[#F3F4F6]"
          >
            {isEdit
              ? "Cambiar número de cotización"
              : "Usar número de cotización personalizado"}
          </Label>
          <p className="text-xs text-[#9CA3AF]">
            {isEdit
              ? "Úsalo solo para recrear documentos históricos. Debe mantener el formato COT-YYYYMM-0000."
              : "Si no se marca, el sistema genera COT-YYYYMM-0000 automáticamente como siempre."}
          </p>
        </div>
      </div>

      {formData.use_custom_quotation_number && (
        <div className="space-y-2 max-w-xs">
          <Label className="text-[#9CA3AF]">Número de cotización</Label>
          <Input
            value={formData.quotation_number}
            onChange={(e) =>
              setFormData({ ...formData, quotation_number: e.target.value })
            }
            className="valhalla-input"
            placeholder="Ej. COT-202604-0001"
            disabled={disabled}
          />
        </div>
      )}

      <div className="flex items-start gap-3">
        <Checkbox
          id={`${mode}-use-custom-issue-date`}
          checked={formData.use_custom_issue_date}
          onCheckedChange={(checked) =>
            setFormData({
              ...formData,
              use_custom_issue_date: Boolean(checked),
              issue_date:
                formData.issue_date || fallbackIssueDate || getTodayDateInput(),
            })
          }
          disabled={disabled}
        />
        <div className="space-y-1">
          <Label
            htmlFor={`${mode}-use-custom-issue-date`}
            className="text-[#F3F4F6]"
          >
            {isEdit
              ? "Cambiar fecha del documento"
              : "Usar fecha personalizada para el documento"}
          </Label>
          <p className="text-xs text-[#9CA3AF]">
            {isEdit
              ? "Recalcula la fecha de vencimiento usando los días de validez."
              : "Si no se marca, la cotización usa la fecha actual como siempre."}
          </p>
        </div>
      </div>

      {formData.use_custom_issue_date && (
        <div className="space-y-2 max-w-xs">
          <Label className="text-[#9CA3AF]">Fecha del documento</Label>
          <Input
            type="date"
            value={formData.issue_date}
            onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
            className="valhalla-input"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

export function QuotationItemsEditor({
  formData,
  t,
  formatMoney,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onItbmsChange,
  onOpenCatalog,
  disabled = false,
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Label className="text-[#9CA3AF]">{t("productsServices")}</Label>
        <div className="flex flex-wrap gap-2">
          {onOpenCatalog ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onOpenCatalog}
              className="border-[#3AB8D6] text-[#3AB8D6] hover:bg-[#3AB8D6]/10"
              disabled={disabled}
            >
              <Package className="w-3 h-3 mr-1" />
              Catálogo
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onAddItem}
            className="border-[#242E42]"
            disabled={disabled}
          >
            <Plus className="w-3 h-3 mr-1" />
            {t("addItem")}
          </Button>
        </div>
      </div>

      {formData.items.map((item, index) => (
        <div
          key={index}
          className="grid grid-cols-2 gap-2 sm:grid-cols-12 sm:items-end"
        >
          <div className="col-span-2 sm:col-span-5">
            <Input
              placeholder={t("serviceDescription")}
              value={item.description}
              onChange={(e) => onUpdateItem(index, "description", e.target.value)}
              className="valhalla-input"
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              placeholder={t("qty")}
              value={item.quantity}
              onChange={(e) =>
                onUpdateItem(index, "quantity", parseFloat(e.target.value) || 0)
              }
              className="valhalla-input"
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              placeholder={t("unitPrice")}
              value={item.unit_price}
              onChange={(e) =>
                onUpdateItem(index, "unit_price", parseFloat(e.target.value) || 0)
              }
              className="valhalla-input"
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              value={formatMoney(item.total)}
              readOnly
              className="valhalla-input bg-[#0B0E14]"
            />
          </div>
          <div className="flex justify-end sm:col-span-1">
            {!disabled && formData.items.length > 1 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onRemoveItem(index)}
                className="text-red-400"
              >
                <Trash className="w-4 h-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <div className="w-full space-y-2 rounded-lg border border-[#242E42] bg-[#0B0E14] p-4 sm:w-72">
          <div className="flex justify-between text-sm">
            <span className="text-[#9CA3AF]">{t("subtotal")}</span>
            <span className="text-[#F3F4F6]">${formatMoney(formData.subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#9CA3AF]">{t("itbmsLabel")}</span>
            <Input
              type="number"
              value={formData.itbms_rate}
              onChange={(e) => onItbmsChange(parseFloat(e.target.value) || 0)}
              className="valhalla-input w-20 text-right"
              disabled={disabled}
            />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#9CA3AF]">ITBMS</span>
            <span className="text-[#F3F4F6]">
              ${formatMoney(formData.itbms_amount)}
            </span>
          </div>
          <div className="flex justify-between border-t border-[#242E42] pt-2">
            <span className="font-semibold text-[#F3F4F6]">{t("totalUSD")}</span>
            <span className="font-['Outfit'] text-xl font-bold text-[#E2B05E]">
              ${formatMoney(formData.total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function QuotationTermsFields({ formData, setFormData, t, disabled = false }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{t("validityDays")}</Label>
          <Input
            type="number"
            value={formData.validity_days}
            onChange={(e) =>
              setFormData({
                ...formData,
                validity_days: parseInt(e.target.value, 10) || 30,
              })
            }
            className="valhalla-input"
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{t("deliveryTime")}</Label>
          <Input
            value={formData.delivery_time}
            onChange={(e) =>
              setFormData({ ...formData, delivery_time: e.target.value })
            }
            className="valhalla-input"
            placeholder={t("deliveryTimePlaceholder")}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{t("paymentMethod")}</Label>
        <Input
          value={formData.payment_terms}
          onChange={(e) =>
            setFormData({ ...formData, payment_terms: e.target.value })
          }
          className="valhalla-input"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{t("warranty")}</Label>
        <Input
          value={formData.warranty}
          onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
          className="valhalla-input"
          placeholder={t("warrantyPlaceholder")}
          disabled={disabled}
        />
      </div>
    </>
  );
}

export function QuotationNotesFields({ formData, setFormData, t }) {
  return (
    <>
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{t("additionalNotes")}</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="valhalla-input"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">Notas internas</Label>
        <Textarea
          value={formData.internal_notes}
          onChange={(e) =>
            setFormData({ ...formData, internal_notes: e.target.value })
          }
          className="valhalla-input"
          placeholder="Solo visible para el equipo interno. No aparece en el PDF."
        />
      </div>
    </>
  );
}
